import { ConfiguredCarCarCode } from '@oneaudi/onegraph-client';
import { NotificationProps } from '@oneaudi/unified-web-alpha-components';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useAppDispatch, useAppState } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { ErrorType } from '~/app/tracking/feld-m-types';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useConfiguredCarVehicleCodeLazyQuery } from '~/graphql/hooks/use-configured-car-queries';
import { state } from '~/storage';

export function useGetAudiCode() {
	const {
		config: { financingInput },
	} = useMarketConfig();
	const { carIdentifier } = useConfigurator();
	const dispatch = useAppDispatch();
	const appState = useAppState();
	const audiCode = appState.audiCode?.data ?? ({} as ConfiguredCarCarCode);
	const shouldDisableButton = appState.audiCode?.loading;
	const [audicodeIdentifier, setAudicodeIdentifier] = useAtom(state.audicodeIdentifier);
	const [audicodeFinancing, setAudicodeFinancing] = useAtom(state.audicodeFinancing);

	const { i18n } = useI18n();

	const { fetchAudicode, loading: isLoadingAudiCode } = useConfiguredCarVehicleCodeLazyQuery();

	const dispatchAudiCode = useCallback(
		(newAudiCode: ConfiguredCarCarCode, loading: boolean) => {
			dispatch({ type: 'SET_AUDICODE', payload: { data: newAudiCode, loading } });
		},
		[dispatch],
	);

	const getAudiCode = useCallback(
		async (callback: (audiCode: ConfiguredCarCarCode) => void) => {
			const stringifiedIdentifier = JSON.stringify(carIdentifier);
			const stringifiedFinancing = JSON.stringify(financingInput);
			if (
				audiCode.id &&
				stringifiedFinancing === audicodeFinancing &&
				stringifiedIdentifier === audicodeIdentifier
			) {
				callback(audiCode);
			} else {
				try {
					dispatchAudiCode({} as ConfiguredCarCarCode, true);

					const result = await fetchAudicode({
						variables: {
							configuredCar: carIdentifier,
							financingInput,
						},
					});
					if (result.data?.configuredCar.code.id) {
						dispatchAudiCode(result.data?.configuredCar.code ?? ({} as ConfiguredCarCarCode), false);
						setAudicodeFinancing(stringifiedFinancing);
						setAudicodeIdentifier(stringifiedIdentifier);
						callback(result.data?.configuredCar.code ?? ({} as ConfiguredCarCarCode));
					} else {
						const _notification: NotificationProps = {
							title: {
								text: i18n('ngc.errors.unexpected.title'),
								tag: 'h1',
							},
							copy: i18n('ngc.errors.unexpected.message'),
							icon: 'caution',
						};
						dispatch({ type: 'SET_NOTIFICATION_DATA', payload: _notification });
						dispatchAudiCode({} as ConfiguredCarCarCode, false);
						callback({} as ConfiguredCarCarCode);
						appTrackingService.trackError({
							name: ErrorType.AUDI_CODE,
							userMessage: i18n('ngc.errors.unexpected.message'),
						});
					}
				} catch (error) {
					console.error('Failed to fetch Audi code:', error);
					dispatchAudiCode({} as ConfiguredCarCarCode, false);
					callback({} as ConfiguredCarCarCode);
				}
			}
		},
		[audiCode, dispatchAudiCode, JSON.stringify(carIdentifier), JSON.stringify(financingInput)],
	);

	return { getAudiCode, isLoadingAudiCode, shouldDisableButton };
}
